import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./App.css";

import { Alert, Button, Form, Modal } from "react-bootstrap";
import AuthService from "./services/auth.service";
import RegisterService from "./services/register.service";
import EventBus from "./common/EventBus";
import PortalShell from "./components/PortalShell";
import Clients from "./components/Clients";
import CreateClient from "./components/CreateClient";
import CreateUser from "./components/CreateUser";
import DisbursementsReport from "./components/DisbursementsReport";
import DisbursementsReportAdmin from "./components/DisbursementsReportAdmin";
import Home from "./components/Home";
import Login from "./components/Login";
import ProcessTransactions from "./components/ProcessTransactions";
import Profile from "./components/Profile";
import Projects from "./components/Projects";
import Register from "./components/Register";
import Users from "./components/Users";

const ROUTE_META = {
  "/home": {
    eyebrow: "Overview",
    title: "Payments command centre",
    description:
      "Track liquidity, payment outcomes, and operational signals from a single corporate portal.",
  },
  "/clients": {
    eyebrow: "Administration",
    title: "Client portfolio",
    description:
      "Manage corporate accounts, balances, and account-to-user relationships with stronger operational visibility.",
  },
  "/users": {
    eyebrow: "Administration",
    title: "User administration",
    description:
      "Control user access, support actions, and internal portal administration from one place.",
  },
  "/createclient": {
    eyebrow: "Administration",
    title: "Create client account",
    description:
      "Onboard a new client profile and link the right operational user from the outset.",
  },
  "/createuser": {
    eyebrow: "Administration",
    title: "Create portal user",
    description:
      "Provision a new operator or administrator with the credentials needed to access the portal.",
  },
  "/processtransactions": {
    eyebrow: "Payments",
    title: "Process transactions",
    description:
      "Upload batches, validate spreadsheet structure, and release disbursements with confidence.",
  },
  "/disbursementreport": {
    eyebrow: "Payments",
    title: "Transaction reporting",
    description:
      "Monitor payment outcomes, reconcile activity, and investigate failed transactions quickly.",
  },
  "/projects": {
    eyebrow: "Payments",
    title: "Projects",
    description:
      "Group disbursements under operational projects and keep reporting aligned to business initiatives.",
  },
  "/adminreport": {
    eyebrow: "Administration",
    title: "Cross-client reporting",
    description:
      "Review transaction activity across the portfolio with executive-level filtering and reporting.",
  },
  "/profile": {
    eyebrow: "Account",
    title: "Profile",
    description: "View the current session and account information for this portal user.",
  },
};

const AUTH_ROUTES = new Set(["/", "/login", "/register"]);

const App = () => {
  const [admin, setAdmin] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const [show, setShow] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [changePasswordMessage, setChangePasswordMessage] = useState("");
  const [loggedInUser, setLoggedInUser] = useState({});
  const navigate = useNavigate();
  const location = useLocation();

  const logOut = useCallback(() => {
    RegisterService.logout();
    setLoggedIn(false);
    setAdmin(false);
    setLoggedInUser({});
    navigate("/login");
  }, [navigate]);

  useEffect(() => {
    const user = AuthService.getCurrentUser();

    if (user) {
      setLoggedInUser(user);
      setLoggedIn(true);
      setAdmin(
        user.userRoles.includes("ADMIN") || user.userRoles.includes("Admin")
      );
    }

    const handleLogoutEvent = () => {
      logOut();
    };

    EventBus.on("logout", handleLogoutEvent);

    return () => {
      EventBus.remove("logout");
    };
  }, [logOut]);

  useEffect(() => {
    if (loggedIn && AUTH_ROUTES.has(location.pathname)) {
      navigate("/home");
    }
  }, [location.pathname, loggedIn, navigate]);

  const pageMeta = useMemo(() => {
    return (
      ROUTE_META[location.pathname] || {
        eyebrow: loggedIn ? "Portal" : "Authentication",
        title: loggedIn ? "PayMe Portal" : "Welcome to PayMe",
        description: loggedIn
          ? "Manage corporate payments in a secure, operationally focused workspace."
          : "Sign in to the corporate payments portal to manage disbursements and reporting.",
      }
    );
  }, [location.pathname, loggedIn]);

  const handleClose = () => {
    setShow(false);
    setOldPassword("");
    setNewPassword("");
    setConfirmNewPassword("");
    setChangePasswordMessage("");
  };

  const handleShow = () => {
    setShow(true);
    setChangePasswordMessage("");
  };

  const handleChangePasswordSubmit = () => {
    if (newPassword !== confirmNewPassword) {
      setChangePasswordMessage("Passwords don't match");
      return;
    }

    RegisterService.changePassword(oldPassword, newPassword).then(
      (response) => {
        if (response.data.succeeded) {
          handleClose();
          logOut();
        } else {
          setChangePasswordMessage(response.data.errors[0].description);
        }
      },
      (error) => {
        const resMessage =
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          error.message ||
          error.toString();
        setChangePasswordMessage(resMessage);
      }
    );
  };

  const routeContent = (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/home" element={<Home />} />
      <Route path="/clients" element={<Clients />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/createclient" element={<CreateClient />} />
      <Route path="/users" element={<Users />} />
      <Route path="/createuser" element={<CreateUser />} />
      <Route path="/profile" element={<Profile />} />
      <Route path="/projects" element={<Projects />} />
      <Route path="/processtransactions" element={<ProcessTransactions />} />
      <Route path="/disbursementreport" element={<DisbursementsReport />} />
      <Route path="/adminreport" element={<DisbursementsReportAdmin />} />
    </Routes>
  );

  return (
    <div className={loggedIn ? "app-root app-root--portal" : "app-root app-root--auth"}>
      {loggedIn ? (
        <PortalShell
          admin={admin}
          currentPath={location.pathname}
          pageMeta={pageMeta}
          user={loggedInUser}
          onLogout={logOut}
          onOpenChangePassword={handleShow}
        >
          {routeContent}
        </PortalShell>
      ) : (
        <div className="auth-shell">
          <div className="auth-shell__panel">
            <span className="portal-pill portal-pill--outline">PayMe Portal</span>
            <h1>Professional payment operations for corporate teams.</h1>
            <p>
              Process disbursements, monitor transaction outcomes, and keep
              audit-sensitive workflows under one secure portal experience.
            </p>

            <div className="auth-shell__highlights">
              <div>
                <span className="bi bi-shield-check" aria-hidden="true" />
                <div>
                  <strong>Secure access</strong>
                  <p>Role-aware access for admins and payment operators.</p>
                </div>
              </div>
              <div>
                <span className="bi bi-graph-up-arrow" aria-hidden="true" />
                <div>
                  <strong>Operational visibility</strong>
                  <p>Track batches, failures, retries, balances, and reports clearly.</p>
                </div>
              </div>
              <div>
                <span className="bi bi-bank" aria-hidden="true" />
                <div>
                  <strong>Corporate-ready workflows</strong>
                  <p>Built for structured payment operations and client servicing.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="auth-shell__content">{routeContent}</div>
        </div>
      )}

      <Modal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Change password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="modal-supporting-text">
            Update your credentials to keep the payments portal secure.
          </p>
          <Form>
            <Form.Group className="mb-3" controlId="changePasswordOldPassword">
              <Form.Label>Old password</Form.Label>
              <Form.Control
                type="password"
                value={oldPassword}
                onChange={(event) => setOldPassword(event.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="changePasswordNewPassword">
              <Form.Label>New password</Form.Label>
              <Form.Control
                type="password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="changePasswordConfirmPassword">
              <Form.Label>Confirm new password</Form.Label>
              <Form.Control
                type="password"
                value={confirmNewPassword}
                onChange={(event) => setConfirmNewPassword(event.target.value)}
              />
            </Form.Group>
          </Form>
          {changePasswordMessage ? (
            <Alert variant="danger" className="mb-0">
              {changePasswordMessage}
            </Alert>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleChangePasswordSubmit}>
            Update password
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default App;
